<template>
	<div class="landing-container" :class="style()" v-scroll="handlePageScroll" v-resize.initial="handleResize">
		<mq-layout mq="desktop">
			<BaseLoader v-if="!hasCategories" />
			<navigation-anchor-menu v-if="!layout_hasSidebar" />
			<recent-orders />
			<adapted-component adapter-name="CouponContainer" path="landing" />
			<template v-for="category in categories">
				<category-detail :category="category" :key="category.id" />
			</template>
		</mq-layout>
		<mq-layout :mq="['mobile', 'tablet']">
			<customer-info-mobile />
			<adapted-component adapter-name="CouponContainer" path="landing" />
			<no-recent-orders />
			<recent-orders />
			<adapted-component adapter-name="MenuCategories" path="menu" />
		</mq-layout>
		<v-dialog/>
	</div>
</template>

<script>
	// Globals
	import EventBus from '@EventBus';
	import store from '@/stores/index';
	import { mapGetters, mapActions } from 'vuex';
	import { CATEGORY, ACCOUNT, ACCOUNT_CHECKOUT, RECENT_ORDERS, LOCATION, FRANCHISOR, APP_STORE } from '@/stores/defs';
	import resize from 'vue-resize-directive';

	// Services
	import allService from '@/services/all.service';
	import locationService from '@/services/location.service';
	import { EPreorder } from '@/services/order.service';
	import recentOrderService from '@/services/recent.orders.service';
	import addressService from '@/services/address.service';
	import { localStorageAdapter } from '@/services/app.service';

	// Mixins
	import storeLoaderMixin from '@Mixin/storeLoaderMixin';
	import waypointMixin from '@Mixin/waypointMixin';
	import timeMixin from '@/components/mixins/timeMixin';
	import flowStateMixin from '@Mixin/flowStateMixin';
	import cartMixin from '@Mixin/cartMixin';
	import locationsMixin from '@Mixin/locationsMixin';
	import upsellMixin from '@Mixin/upsellMixin';
	import headerMixin from '@Mixin/headerMixin';
	import serviceMixin from '@Mixin/serviceMixin';
	import layoutMixin from '@Mixin/layoutMixin';
	import welcomeMixin from '@Mixin/welcomeMixin';

	// Utilities
	import DateManipulator from '@/utils/DateManipulator';
	import { delayCall } from "@/utils/helper.functions";
	import { setTimeout } from 'timers';
	import { filter, isEmpty, throttle } from 'lodash';
	import * as moment from "moment";
	import 'moment-timezone';

	// Constants
	import { ERROR_MSG } from '@/common/messages/error.msg';
	import { EServiceType } from '@/models/interfaces/Orders/EServiceType';

	// Model
	import LocationModel from '@Model/LocationModel';

	const CustomerInfoMobile = (resolve) => {
		require.ensure([], () => {
			resolve(require('@Header/CustomerInfoMobile.vue'));
		});
	};
	const NoRecentOrders = (resolve) => {
		require.ensure([], () => {
			resolve(require('@Orders/NoRecentOrders.vue'));
		});
	};
	const RecentOrderContainer = (resolve) => {
		require.ensure([], () => {
			resolve(require('@Orders/RecentOrderContainer.vue'));
		});
	};
	const MenuCategoryDetail = (resolve) => {
		require.ensure([], () => {
			resolve(require('@Menu/MenuCategoryDetail.vue'));
		});
	};
	const NavigationAnchorMenu = (resolve) => {
		require.ensure([], () => {
			resolve(require('@/components/header/NavigationAnchorMenu/NavigationAnchorMenu.vue'));
		});
	};
	const CouponField = (resolve) => {
		require.ensure([], () => {
			resolve(require('@/components/checkout/CouponField.vue'));
		});
	};
	const _Adapter = (resolve) => {
		require.ensure([], () => {
			const vue = require('@Adapter');
			resolve(vue);
		});
	};
	import NewStoreWelcome from '@Landing/NewStoreWelcome';
	import ContentModal from '@ContentModal';

	export default {
		mixins: [
			storeLoaderMixin,
			waypointMixin,
			timeMixin,
			flowStateMixin,
			cartMixin,
			locationsMixin,
			upsellMixin,
			headerMixin,
			serviceMixin,
			layoutMixin,
			welcomeMixin,
		],
		directives: {
			resize,
		},
		components: {
			'customer-info-mobile': CustomerInfoMobile,
			'no-recent-orders': NoRecentOrders,
			'recent-orders': RecentOrderContainer,
			'category-detail': MenuCategoryDetail,
			'coupon-field': CouponField,
			'navigation-anchor-menu': NavigationAnchorMenu,
			'adapted-component': _Adapter,
		},
		data() {
			return {
				lastScrollTop: 0,
				lastHeaderStateExpanded: true,
			};
		},
		computed: {
			...mapGetters({
				categories: CATEGORY.GET_AVAILABLE,
				hasCategories: CATEGORY.HAS_CATEGORIES,
				loggedIn: ACCOUNT.GET_LOGGED_IN,
				orderTime: ACCOUNT_CHECKOUT.GET_ORDER_TIME,
				recentOrders: RECENT_ORDERS.GET_RECENT_ORDERS,
				location: LOCATION.GET_LOCATION,
				activeOrder: RECENT_ORDERS.GET_ACTIVE_ORDER,
				deliveryAddress: ACCOUNT_CHECKOUT.GET_DELIVERY_ADDRESS,
			}),
		},
		methods: {
			...mapActions({
				setDeliveryAddress: ACCOUNT_CHECKOUT.SET_DELIVERY_ADDRESS,
				setServiceType: ACCOUNT_CHECKOUT.SET_SERVICE_TYPE,
				setActiveOrder: RECENT_ORDERS.SET_ACTIVE_ORDER,
				setPreorder: ACCOUNT_CHECKOUT.SET_PREORDER,
				setFranchisor: FRANCHISOR.SET_FRANCHISOR,
			}),
			// TODO remove flow state code.
			// All of this should be in the configuration.
			greetingActive() {
				return this.getFlowState('greeting');
			},
			serviceChooserActive() {
				const exited = this.exitedFlows(['greeting']);
				const active = this.getFlowState('service');
				return exited && active;
			},
			locatorActive() {
				const exited = this.exitedFlows(['greeting','service']);
				const active = this.getFlowState('locator');
				if (this.hasSelectedLocation && active) {
					this.setFlowStep({
						name: 'locator',
						step: 'active',
						value: false,
					});
					return false;
				}
				return exited && active;
			},
			handleMissingComponent(name) {
				let flowName = '';
				if (name === 'ServiceChooser') {
					flowName = 'service';
				}
				if (name === 'StoreLocator') {
					flowName = 'locator';
				}
				if (flowName === '') {
					return;
				}
				this.setFlowStep({
					name: flowName,
					step: 'active',
					value: false,
				});
			},
			collapseHeader(event) {
				const scrollTop = event.currentTarget.scrollY;
				const deltaScroll = scrollTop - this.lastScrollTop;
				const headerExpanded = this.lastHeaderStateExpanded;
				const absoluteDelta = Math.abs(deltaScroll);
				const pageMin = (scrollTop > 40);
				let scrollFlag = true;
				if (pageMin && absoluteDelta < 10) {
					scrollFlag = false;
				}
				if (pageMin && deltaScroll > 0 && headerExpanded && scrollFlag) {
					EventBus.$emit('collapse-app-header');
					this.lastHeaderStateExpanded = false;
				}
				if (deltaScroll <= 0 && !headerExpanded && scrollFlag) {
					EventBus.$emit('expand-app-header');
					this.lastHeaderStateExpanded = true;
				}
				this.lastDeltaScroll = deltaScroll;
				this.lastScrollTop = scrollTop;
			},
			handlePageScroll(event) {
				const exec = throttle(this.collapseHeader, 500);
				exec(event);
				this.throttleUpdateAnchor(event);
			},
			showNewStoreWelcomeModal() {
				if (this.location && !this.location.id) {
					return;
				}
				if (this.loggedIn || this.cartItemCount > 0) {
					this.welcome_setShowNewStore(false);
					return;
				}
				if (this.welcome_getShowNewStore !== null) {
					this.welcome_setShowNewStore(false);
					return;
				}
				if (this.location) {
					const useWelcome = this.location.newOnlineStoreWelcome;
					this.welcome_setShowNewStore(useWelcome);
				}
				const modalWidth = (this.$mq === 'mobile') ? '100%' : '65%';
				const title = (this.$mq === 'mobile') ? 'Welcome' : 'Welcome to our new online store';
				if (this.welcome_getShowNewStore) {
					this.$modal.show(ContentModal, {
						contentComponent: NewStoreWelcome,
						header: {
							title: title,
							showCloseButton: true,
							showBackButton: false,
						},
						footer: {
							showFooter: false,
						},
					}, {
						name: 'contentModal',
						width: modalWidth,
						height: 'auto',
						scrollable: false,
						clickToClose: false,
						delay: 500,
					});
				}
			},
			setHeaderForLandingPage() {
				if (this.$mq !== 'mobile') {
					return;
				}
				const appConstants = this.$appConstants;
				const header = appConstants.header;
				const homeItems = header.routes.home;
				this._setHeaderItems(homeItems);
				const storeName = appConstants.storeName;
				this._setHeaderTitle(storeName);
			},
			handleResize() {
				this.setHeaderForLandingPage();
				EventBus.$emit('recalculate-header');
				EventBus.$emit('scrolledInView');
			},
			style() {
				return {
					'cart-active': this.hasItems(),
				}
			},
		},
		mounted() {
			// TODO make configurable (default serviceType if none chosen)
			if (!this.getServiceType) {
				this.setServiceTakeout();
			}
			this.setHeaderForLandingPage();
			this.showNewStoreWelcomeModal();
		},
		created() {
			this.loadAllFromStorage();
			const storedLocation = locationService.getLocationFromStorage();
			let lb = null;
			// default to location in store, else stored location (should be same)
			if (this.location && this.location.id) {
				lb = this.location.id;
			} else if (storedLocation && storedLocation.id) {
				lb = storedLocation.id;
			}
			if (this.$appConstants.showRecommendedItems === '1') {
				this.setShowUpsellOn(); // TODO rename upsell to recommended (this will cause confusion with upsell feature)
			}
			allService.load(({ menu, customer, offers, recentOrders, location, activeOrder, franchisor }, errObj) => {
				if (errObj && errObj.msg === ERROR_MSG.APP.ONLINE_ORDERING_DISABLED) {
						// remove current location
						const emptyLocation = new LocationModel({});
						locationService.setLocationForStorage(emptyLocation);
						this.setLocation(emptyLocation);
						this.triggerModal({
							component: StoreLocator,
							showCloseButton: false,
							classes: "global-modal",
							title: "Locations",
						})
				} else {
					if (menu) {
						this.setCategories(menu.categories);
						this.setItems(menu.items);
						EventBus.$emit('store-loaded');
						this.setOptions(menu.options);
						this.setOptionExceptions(menu.optionExceptions);
					}
					// TODO move this into service, service should be the only object to communicate with store and storage
					let customerStore = store.getters[ACCOUNT.GET_CUSTOMER];
					if (customerStore && !customer.id) {
						this.setCustomer(customerStore);
					} else {
						this.setCustomer(customer);
						this.setOffers(offers);
					}

					if (location) {
						this.setLocation(location);
					}
					this.setFranchisor(franchisor);
					this.setRecentOrders(recentOrders);

					if (customer && customer.id) {
						this.setLoggedIn(true);

						// set delivery address
						// // TODO this needs to be a configurable setting. Also needs to check against currently set state.
						if (activeOrder && location && DateManipulator.isMomentToday(moment(activeOrder.order.promise_date * 1000))) {
							this.setActiveOrder(recentOrderService.convertSingleOrder(activeOrder));
						} else {
							this.setActiveOrder(null);
						}
						
						delayCall(() => this.storeCustomer(customer));
					}
					if (location && location.id) {
						delayCall(() => this.storeLocation(location));
						this.setOrderTime(this.nextAvailableTime)
					}

					if (menu) {
						delayCall(() => {
							this.storeCategories(menu.categories);
							this.storeOptions(menu.options);
							this.storeOptionExceptions(menu.optionExceptions);
						});
					}
					if (location && location.id && location.locationHours[0].isClosed) {
						this.$modal.show("dialog", {
							text: ERROR_MSG.ORDER.ORDER_TIME.STORE_CLOSED
						});
					}
				}
			}, lb);
			EventBus.$on('store-loaded', throttle(this._initializeCart, 2000));
			EventBus.$on('no-component', this.handleMissingComponent);
			EventBus.$on('new-welcome-modal', () => {
				this.showNewStoreWelcomeModal();
			});
		},
		beforeDestroy() {
			if (this.layout_checkDynamicPadding()) {
				const rootElement = this.$root.$el;
				const mainContainer = rootElement.querySelector('#main-container');
				mainContainer.style.paddingTop = 0;
			}
		},
		destroyed() {
			EventBus.$off('no-component');
			EventBus.$off('new-welcome-modal');
			EventBus.$off('store-loaded');
		},
		beforeRouteEnter(to, from, next) {
			if (from.name) {
				next((vue) => {
					vue.setHeaderForLandingPage();
					vue.welcome_setShowNewStore(false);
				});
				return;
			}
			next((vue) => {
				vue.setHeaderForLandingPage();
			});
		}
	};
</script>

<style lang="scss" scoped>
    @import 'globalStyles';

	.landing-container {
		position: relative;

		padding-bottom: 20px;
		background-color: $landingPageBackground;

		.section-title {
			margin: auto;
			padding: 2em 0 1em;
			width: $maxWidthContainerDesktop;
			
			color: $categoryTitles;
			font-weight: bold;
			font-size: 1.5em;
		}
	}
	@include desktop {
		.landing-container {
			margin-top: 0;
			padding-top: 0;
		}
	}
	@include mobile {
		.landing-container {
			.section-title {
				width: 90vw;
				padding: 1em 0 0;
			}
			&.cart-active {
				padding-top: 52px;
			}
		}
	}
	@include tablet {
		.landing-container {
			&.cart-active {
				padding-top: 52px;
			}
		}
	}
</style>


// WEBPACK FOOTER //
// src/components/LandingPage.vue